import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

interface ExpandableReadMoreTextProps {
  id?: string;
  text: string;
  numberOfLines?: number;
  className?: string;
}

interface TextDimensions {
  overflows: boolean;
  truncatedText: string;
  collapsedHeight: number;
  expandedHeight: number;
}

// TODO: Functional for now, check for performance later

const ExpandableReadMoreText = ({
  id,
  text,
  numberOfLines = 3,
  className,
}: ExpandableReadMoreTextProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [dims, setDims] = useState<TextDimensions>({
    overflows: false,
    truncatedText: text,
    collapsedHeight: 0,
    expandedHeight: 0,
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const hiddenRef = useRef<HTMLParagraphElement>(null);
  const measureSpanRef = useRef<HTMLSpanElement>(null);

  const calculateTextDimensions = useCallback(() => {
    const hiddenEl = hiddenRef.current;
    const measureSpan = measureSpanRef.current;
    if (!hiddenEl || !measureSpan) return;

    measureSpan.textContent = text;

    const computedStyle = globalThis.getComputedStyle(hiddenEl);
    const lineHeightStr = computedStyle.lineHeight;
    const lineHeight =
      lineHeightStr === "normal"
        ? Number.parseFloat(computedStyle.fontSize) * 1.4
        : Number.parseFloat(lineHeightStr);

    const expandedHeight = hiddenEl.scrollHeight;
    const collapsedHeight = Math.round(lineHeight * (numberOfLines - 1));

    if (expandedHeight <= collapsedHeight + 1) {
      setDims({
        overflows: false,
        truncatedText: text,
        collapsedHeight,
        expandedHeight,
      });
      return;
    }

    // Binary search: find the max character count that fits within (numberOfLines - 1) lines,
    // leaving the last line clear for the inline "...more" button.
    const targetHeight = lineHeight * (numberOfLines - 1);
    let lo = 0;
    let hi = text.length;

    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      measureSpan.textContent = text.slice(0, mid);
      if (hiddenEl.scrollHeight <= targetHeight + 1) {
        lo = mid;
      } else {
        hi = mid - 1;
      }
    }

    measureSpan.textContent = text;

    const truncatedText = text.slice(0, Math.max(0, lo - 10)).trimEnd();
    setDims({
      overflows: true,
      truncatedText,
      collapsedHeight,
      expandedHeight,
    });
  }, [text, numberOfLines]);

  useLayoutEffect(() => {
    calculateTextDimensions();
  }, [calculateTextDimensions]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(calculateTextDimensions);
    observer.observe(container);
    return () => observer.disconnect();
  }, [calculateTextDimensions]);

  const { overflows, truncatedText, collapsedHeight, expandedHeight } = dims;
  const height = overflows
    ? isExpanded
      ? expandedHeight
      : collapsedHeight
    : undefined;

  const textClasses = `text-sm tracking-normal leading-relaxed${className ? ` ${className}` : ""}`;

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      {/* Hidden measurement layer — same styles as visible text to get accurate line metrics */}
      <p
        ref={hiddenRef}
        aria-hidden
        className={textClasses}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          visibility: "hidden",
          pointerEvents: "none",
        }}
      >
        <span ref={measureSpanRef}>{text}</span>
      </p>

      {/* Animated visible layer */}
      <div
        style={{
          height,
          overflow: "hidden",
          transition: "height 300ms ease",
        }}
      >
        <p id={id} className={textClasses}>
          {overflows && !isExpanded ? (
            <>
              {truncatedText}
              <span>...</span>
              <button
                type="button"
                className="ml-1 cursor-pointer font-semibold text-black"
                onClick={() => setIsExpanded(true)}
              >
                more
              </button>
            </>
          ) : (
            text
          )}
        </p>
      </div>
    </div>
  );
};

export default ExpandableReadMoreText;
